import React, { useState } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Faction, FactionCreate, FactionUpdate, AffiliationType } from '../../types/orbat';
import FactionCard from './FactionCard';
import AddFactionModal from './AddFactionModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface FactionListProps {
    factions: Faction[];
    affiliations: AffiliationType[];
    selectedFactionId: string | null;
    isLoading: boolean;
    error: string | null;
    onSelectFaction: (factionId: string) => void;
    onCreateFaction: (data: FactionCreate) => Promise<void>;
    onUpdateFaction: (factionId: string, data: FactionUpdate) => Promise<void>;
    onDeleteFaction: (factionId: string) => Promise<void>;
}

/**
 * Component displaying the list of factions with add/edit/delete functionality.
 */
const FactionList: React.FC<FactionListProps> = ({
    factions,
    affiliations,
    selectedFactionId,
    isLoading,
    error,
    onSelectFaction,
    onCreateFaction,
    onUpdateFaction,
    onDeleteFaction,
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingFaction, setEditingFaction] = useState<Faction | null>(null);
    const [deletingFaction, setDeletingFaction] = useState<Faction | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const handleAddClick = () => {
        setModalError(null);
        setShowAddModal(true);
    };

    const handleEditClick = (faction: Faction) => {
        setModalError(null);
        setEditingFaction(faction);
    };

    const handleDeleteClick = (faction: Faction) => {
        setDeletingFaction(faction);
    };

    const handleCreateSubmit = async (data: FactionCreate | FactionUpdate) => {
        setModalLoading(true);
        setModalError(null);
        try {
            await onCreateFaction(data as FactionCreate);
            setShowAddModal(false);
        } catch (err) {
            setModalError(err instanceof Error ? err.message : 'Failed to create faction');
            throw err;
        } finally {
            setModalLoading(false);
        }
    };

    const handleUpdateSubmit = async (data: FactionCreate | FactionUpdate) => {
        if (!editingFaction) return;
        setModalLoading(true);
        setModalError(null);
        try {
            await onUpdateFaction(editingFaction.id, data as FactionUpdate);
            setEditingFaction(null);
        } catch (err) {
            setModalError(err instanceof Error ? err.message : 'Failed to update faction');
            throw err;
        } finally {
            setModalLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingFaction) return;
        setModalLoading(true);
        try {
            await onDeleteFaction(deletingFaction.id);
            setDeletingFaction(null);
        } catch (err) {
            console.error('Failed to delete faction:', err);
        } finally {
            setModalLoading(false);
        }
    };

    return (
        <div className="faction-list">
            <div className="faction-list__header">
                <h5>Factions</h5>
            </div>

            <div className="faction-list__content">
                {isLoading && factions.length === 0 ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2 mb-0 text-muted">Loading factions...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger" className="mb-0">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {error}
                    </Alert>
                ) : (
                    factions.map((faction) => (
                        <FactionCard
                            key={faction.id}
                            faction={faction}
                            affiliations={affiliations}
                            isSelected={faction.id === selectedFactionId}
                            onSelect={onSelectFaction}
                            onEdit={handleEditClick}
                            onDelete={handleDeleteClick}
                        />
                    ))
                )}

                {/* Add Faction Card */}
                <Card className="add-faction-card" onClick={handleAddClick}>
                    <div className="add-faction-card__body">
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Add Faction</span>
                    </div>
                </Card>
            </div>

            {/* Add Faction Modal */}
            <AddFactionModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={handleCreateSubmit}
                affiliations={affiliations}
                isLoading={modalLoading}
                error={modalError}
            />

            {/* Edit Faction Modal */}
            <AddFactionModal
                show={!!editingFaction}
                onHide={() => setEditingFaction(null)}
                onSubmit={handleUpdateSubmit}
                faction={editingFaction}
                affiliations={affiliations}
                isLoading={modalLoading}
                error={modalError}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                show={!!deletingFaction}
                onHide={() => setDeletingFaction(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete Faction"
                message={`Are you sure you want to delete "${deletingFaction?.name}"? This will also delete all unit groups and units within this faction.`}
                isLoading={modalLoading}
            />
        </div>
    );
};

export default FactionList;
